import { Link } from "react-router-dom";
import Navigation from "@/components/layout/Navigation";
import Footer from "@/components/layout/Footer";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="py-16 md:py-24">
        <article className="container mx-auto px-4 max-w-3xl">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-primary mb-2">
            Privacy &amp; Cookies Policy
          </h1>
          <p className="font-mono text-sm text-muted-foreground mb-10">
            Last updated: 20 February 2025
          </p>

          <div className="prose prose-sm md:prose-base max-w-none space-y-8 text-foreground/80 leading-relaxed text-left">
            {/* ── Intro ── */}
            <section>
              <p>
                This policy explains how Rebecca Ryell Travel ("we", "us", "our") collects, uses, and
                protects your personal data when you use this website. We keep things straightforward
                because legal jargon isn't anyone's idea of a good time.
              </p>
              <p>
                We comply with the UK General Data Protection Regulation (UK GDPR) and the Data
                Protection Act 2018.
              </p>
            </section>

            {/* ── Who we are ── */}
            <section>
              <h2 className="font-display text-xl font-bold text-primary mt-0">Who We Are</h2>
              <p>
                Rebecca Ryell Travel is a sole-trader coaching business based in the United Kingdom.
                Rebecca Ryell is the data controller responsible for your personal data. If you have
                questions, you can reach us at{" "}
                <a href="mailto:hello@rebeccaryell.com" className="text-accent hover:underline">
                  hello@rebeccaryell.com
                </a>.
              </p>
            </section>

            {/* ── What we collect ── */}
            <section>
              <h2 className="font-display text-xl font-bold text-primary">What We Collect</h2>
              <p>We only collect what we genuinely need:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>Email address</strong> — when you sign up to our mailing list, join the
                  adventure list, or register interest in the book waitlist.
                </li>
                <li>
                  <strong>Name</strong> — if you provide it during a booking or enquiry.
                </li>
                <li>
                  <strong>Usage data</strong> — basic, anonymised analytics (pages visited, time on
                  site) to help us improve the website. No fingerprinting, no ad tracking.
                </li>
              </ul>
            </section>

            {/* ── Why we collect it ── */}
            <section>
              <h2 className="font-display text-xl font-bold text-primary">Why We Collect It</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>To send you the content you signed up for (newsletters, field notes, updates).</li>
                <li>To respond to your enquiries or booking requests.</li>
                <li>To improve the website experience.</li>
              </ul>
              <p>
                <strong>Legal basis:</strong> Consent (you actively opt in) and Legitimate Interest
                (basic analytics to keep the site running well).
              </p>
            </section>

            {/* ── How we store it ── */}
            <section>
              <h2 className="font-display text-xl font-bold text-primary">How We Store Your Data</h2>
              <p>
                Your data is stored securely using industry-standard encryption. We use trusted
                third-party services (cloud hosting and database providers) that comply with UK GDPR
                requirements. We never sell, rent, or trade your personal information.
              </p>
            </section>

            {/* ── Your rights ── */}
            <section>
              <h2 className="font-display text-xl font-bold text-primary">Your Rights</h2>
              <p>Under UK GDPR you have the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Access</strong> — request a copy of the data we hold about you.</li>
                <li><strong>Rectification</strong> — ask us to correct inaccurate data.</li>
                <li><strong>Erasure</strong> — ask us to delete your data ("right to be forgotten").</li>
                <li><strong>Restrict processing</strong> — limit how we use your data.</li>
                <li><strong>Object</strong> — opt out of marketing at any time.</li>
                <li><strong>Data portability</strong> — receive your data in a structured format.</li>
              </ul>
              <p>
                To exercise any of these rights, email us at{" "}
                <a href="mailto:hello@rebeccaryell.com" className="text-accent hover:underline">
                  hello@rebeccaryell.com
                </a>. We'll respond within 30 days.
              </p>
            </section>

            {/* ── Cookies ── */}
            <section>
              <h2 className="font-display text-xl font-bold text-primary">Cookies</h2>
              <p>
                Cookies are small text files stored on your device. Here's what we use:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border rounded-lg">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left p-3 font-display font-bold text-primary">Type</th>
                      <th className="text-left p-3 font-display font-bold text-primary">Purpose</th>
                      <th className="text-left p-3 font-display font-bold text-primary">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-border">
                      <td className="p-3 font-medium">Essential</td>
                      <td className="p-3">Keep the site running (session management, security).</td>
                      <td className="p-3">Session</td>
                    </tr>
                    <tr className="border-t border-border">
                      <td className="p-3 font-medium">Analytics</td>
                      <td className="p-3">Anonymised usage data to help us improve the site.</td>
                      <td className="p-3">Up to 12 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-3">
                We do <strong>not</strong> use advertising or tracking cookies. You can disable cookies
                in your browser settings, though some site features may not work as expected.
              </p>
            </section>

            {/* ── Third parties ── */}
            <section>
              <h2 className="font-display text-xl font-bold text-primary">Third-Party Services</h2>
              <p>
                We may share limited data with trusted service providers who help run the site (e.g.
                hosting, email delivery). These providers are contractually required to protect your
                data and only process it on our behalf. We do not share data with advertisers.
              </p>
            </section>

            {/* ── Children ── */}
            <section>
              <h2 className="font-display text-xl font-bold text-primary">Children's Privacy</h2>
              <p>
                This website is aimed at parents and guardians. We do not knowingly collect personal
                data from children under 16. If you believe a child has provided us with personal
                information, please contact us and we will delete it promptly.
              </p>
            </section>

            {/* ── Changes ── */}
            <section>
              <h2 className="font-display text-xl font-bold text-primary">Changes to This Policy</h2>
              <p>
                We may update this policy from time to time. Any changes will be posted on this page
                with an updated "last updated" date. For significant changes, we'll let you know via
                email where possible.
              </p>
            </section>

            {/* ── Complaints ── */}
            <section>
              <h2 className="font-display text-xl font-bold text-primary">Complaints</h2>
              <p>
                If you're unhappy with how we've handled your data, please contact us first so we can
                try to put it right. You also have the right to lodge a complaint with the{" "}
                <a
                  href="https://ico.org.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  Information Commissioner's Office (ICO)
                </a>.
              </p>
            </section>
          </div>

          {/* Back link */}
          <div className="mt-12">
            <Link
              to="/"
              className="font-mono text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default Privacy;
